import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Package, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Checkbox } from '@/app/components/ui/checkbox';
import { apiClient } from '@/lib/api-client';
import { handleOrganizationSwitchSuccess } from '@/hooks/use-memberships';
import { useAuth } from '@/contexts/auth-context';
import { confirmDiscardOfflineWork, holdOfflineReplay } from '@/lib/offline/session-offline-data';

const inviteSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type InviteFormData = z.infer<typeof inviteSchema>;

interface InvitationDetails {
  email: string;
  account_exists: boolean;
  organization_name: string;
  terms_url: string;
  privacy_url: string;
  accepts_for_organization: boolean;
}

export function InvitePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, refreshUser } = useAuth();
  const [error, setError] = useState<string | string[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [legalAccepted, setLegalAccepted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiClient.get<InvitationDetails>(`/api/v1/invitations/${token}/`);
        setInvitation(response.data);
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
        const msg = axiosErr?.response?.data?.error;
        if (typeof msg === 'string') {
          setError(msg);
        } else {
          setError('This invitation is invalid or has expired.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  const onSubmit = async (data: InviteFormData) => {
    if (!token) return;

    setError(null);
    setIsSubmitting(true);

    if (!(await confirmDiscardOfflineWork('switch-org'))) {
      setIsSubmitting(false);
      return;
    }
    const restore = holdOfflineReplay();
    let claimSucceeded = false;
    try {
      // The claim endpoint sets httpOnly auth cookies on success.
      await apiClient.post(
        '/api/v1/invitations/claim/',
        {
          token,
          first_name: data.firstName,
          last_name: data.lastName,
          username: data.username,
          password: data.password,
          legal_accepted: legalAccepted,
        }
      );
      claimSucceeded = true;

      await handleOrganizationSwitchSuccess(queryClient);
      await refreshUser();
      // "/" picks the right home for the role (My Dashboard for crew).
      navigate('/', { replace: true });
    } catch (err: unknown) {
      if (!claimSucceeded) restore();
      const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
      const status = axiosErr?.response?.status;
      const data = axiosErr?.response?.data;

      if (status === 404) {
        setError('This invitation is invalid or has expired.');
      } else if (data) {
        // API may return {"error": "string"} or field-level errors like {"password": ["msg1", "msg2"]}
        if (typeof data.error === 'string') {
          setError(data.error);
        } else if (Array.isArray(data.error)) {
          setError(data.error.map(String));
        } else {
          const messages: string[] = [];
          for (const value of Object.values(data)) {
            if (Array.isArray(value)) {
              messages.push(...value.map(String));
            } else if (typeof value === 'string') {
              messages.push(value);
            }
          }
          setError(messages.length > 0 ? messages : 'Failed to complete setup. Please try again.');
        }
      } else {
        setError('Failed to complete setup. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const acceptExistingAccountInvitation = async () => {
    if (!token) return;

    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(`/invite/${token}`)}`);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    if (!(await confirmDiscardOfflineWork('switch-org'))) {
      setIsSubmitting(false);
      return;
    }
    const restore = holdOfflineReplay();
    let claimSucceeded = false;
    try {
      await apiClient.post('/api/v1/invitations/claim/', {
        token,
        legal_accepted: legalAccepted,
      });
      claimSucceeded = true;
      await handleOrganizationSwitchSuccess(queryClient);
      await refreshUser();
      // "/" picks the right home for the role (My Dashboard for crew).
      navigate('/', { replace: true });
    } catch (err: unknown) {
      if (!claimSucceeded) restore();
      const axiosErr = err as { response?: { data?: Record<string, unknown> } };
      const message = axiosErr.response?.data?.error;
      setError(
        typeof message === 'string'
          ? message
          : 'Failed to accept this invitation. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-500">Loading invitation...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!invitation && error) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <CardTitle className="text-2xl font-bold">Invalid Invitation</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button className="w-full mt-4" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1 flex flex-col items-center">
        <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-2">
          <Package className="w-7 h-7 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold">Accept invitation</CardTitle>
        <CardDescription>
          {invitation?.account_exists
            ? 'Sign in with your existing account to join this organization'
            : 'Set up your account to get started'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {Array.isArray(error) ? (
                  <ul className="list-disc pl-4 space-y-1">
                    {error.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                ) : (
                  error
                )}
              </AlertDescription>
            </Alert>
          )}

          {invitation && (
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={invitation.email} disabled className="bg-gray-50" />
            </div>
          )}

          {invitation && (
            <div className="flex items-start gap-3 rounded-lg border p-4">
              <Checkbox
                id="invite-legal-acceptance"
                checked={legalAccepted}
                onCheckedChange={(checked) => setLegalAccepted(checked === true)}
                disabled={isSubmitting}
              />
              <Label
                htmlFor="invite-legal-acceptance"
                className="cursor-pointer text-sm font-normal leading-6"
              >
                I have read and agree to the{' '}
                <a
                  href={invitation.terms_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-blue-600 underline"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href={invitation.privacy_url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-blue-600 underline"
                >
                  Privacy Policy
                </a>
                {invitation.accepts_for_organization
                  ? `, and I'm authorised to accept them on behalf of ${invitation.organization_name}`
                  : ''}
                .
              </Label>
            </div>
          )}

          {invitation?.account_exists ? (
            <Button
              type="button"
              className="w-full"
              disabled={isSubmitting || !legalAccepted}
              onClick={acceptExistingAccountInvitation}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : isAuthenticated ? (
                'Accept Invitation'
              ) : (
                'Sign In to Accept'
              )}
            </Button>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first-name">First name</Label>
                  <Input
                    id="first-name"
                    type="text"
                    autoComplete="given-name"
                    {...register('firstName')}
                    disabled={isSubmitting}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-500">{errors.firstName.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-name">Last name</Label>
                  <Input
                    id="last-name"
                    type="text"
                    autoComplete="family-name"
                    {...register('lastName')}
                    disabled={isSubmitting}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-500">{errors.lastName.message}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="john.doe"
                  {...register('username')}
                  disabled={isSubmitting}
                />
                {errors.username && (
                  <p className="text-sm text-red-500">{errors.username.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  disabled={isSubmitting}
                />
                {errors.password && (
                  <p className="text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  {...register('confirmPassword')}
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting || !legalAccepted}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  'Complete Setup'
                )}
              </Button>
            </form>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
